/**
 * Панель решений игрока. Три раздела с единой структурой (шапка: заголовок + состояние):
 *   «Прогноз» — направление и число пунктов;
 *   «Ставка» — строка состояния хода, сумма, действия;
 *   «Предварительное действие» — одноразовое действие на ближайший ход.
 * Горячие клавиши: ↑/↓ — направление, +/− — пункты (Shift — ×4), F — пас, C — чек/колл,
 * B — ставка/райз, A — олл-ин. Перед пасом и олл-ином — подтверждение.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AllowedActions, AutoMode, GameView } from '@mt/protocol';
import { client, useApp } from '../net/client';
import { cr, pts, secondsLeft } from '../lib/format';
import { updateSettings, useSettings } from '../lib/settings';
import { num, t, type TKey } from '../i18n';
import { Dialog } from './Dialog';
import { TurnBar } from './Seats';

const toMinor = (credits: number) => Math.round(credits * 100);
const mmss = (s: number | null) => (s === null ? '—' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);
/** Секунды для подписей «· 12 с»; null — прочерк. */
const sec = (s: number | null) => (s === null ? '—' : num(s, 0));
const ROUND_OF: Partial<Record<GameView['stage'], number>> = {
  round1: 1,
  round2: 2,
  round3: 3,
};
/** Что раскрывается вместе со следующим кругом (ключи словаря; текст — на текущем языке при показе). */
const NEXT_REVEAL: Record<number, TKey> = {
  1: 'table.controls.nextRevealScale',
  2: 'table.controls.nextRevealComposition',
};

export function Controls({ game, now }: { game: GameView; now: number }) {
  const me = game.me;
  const tick = game.tick;
  const settings = useSettings();
  const pending = useApp((s) => s.pending.size > 0);
  const [pointsDraft, setPointsDraft] = useState<string>('');
  const [amount, setAmount] = useState<number>(0);
  const [confirm, setConfirm] = useState<null | 'fold' | 'allin'>(null);
  const [allinPoints, setAllinPoints] = useState('');
  const a = me.allowed;
  const locked = !['prep', 'round1', 'round2', 'round3'].includes(game.stage);
  const meSeat = game.seats.find((s) => s.isMe);
  const folded = meSeat?.status === 'folded';
  const allIn = meSeat?.status === 'allin';
  const canEditForecast = !locked && !folded;
  const round = ROUND_OF[game.stage] ?? 0;
  // направление выбирается в подготовке и первых directionRounds кругах (режим: 2 или 1), дальше меняются только пункты
  const canEditDirection = canEditForecast && (game.stage === 'prep' || (round > 0 && round <= game.directionRounds));
  const open = !!game.instrument;

  useEffect(() => setPointsDraft(me.forecast.points === null ? '' : pts(me.forecast.points)), [me.forecast.points]);

  // сумма ставки/райза по умолчанию — минимальная
  const range = a?.bet ?? a?.raise ?? null;
  useEffect(() => {
    if (range) setAmount(range.min);
  }, [range?.min, range?.max, game.stage]);

  const sendPoints = (v: number | null) => {
    if (v !== null && (v <= 0 || Math.abs(v / tick - Math.round(v / tick)) > 1e-9)) {
      client.toast('error', t('table.controls.pointsInvalid', { tick: pts(tick) }));
      return;
    }
    client.forecast({ points: v });
  };
  const stepPoints = (d: number) => {
    const cur = me.forecast.points ?? 0;
    sendPoints(Math.max(tick, Math.round((cur + d) / tick) * tick));
  };
  const commitDraft = () => {
    const raw = pointsDraft.trim();
    if (raw === '') return me.forecast.points !== null && sendPoints(null);
    const v = Number(raw.replace(',', '.'));
    if (!Number.isFinite(v)) return client.toast('error', t('table.controls.pointsEnter'));
    if (v !== me.forecast.points) sendPoints(v);
  };

  const doFold = () => (settings.confirmFold ? setConfirm('fold') : client.act('fold'));
  const doAllIn = () => {
    setAllinPoints(me.forecast.points === null ? '' : pts(me.forecast.points));
    setConfirm('allin');
  };
  const doCheckCall = () => {
    if (a?.check) client.act('check');
    else if (a?.call) {
      if (a.call.allIn) doAllIn();
      else client.act('call');
    }
  };
  const doBetRaise = () => {
    if (!range) return;
    const v = Math.min(Math.max(amount, range.min), range.max);
    if (v - (meSeat?.roundBet ?? 0) >= (meSeat?.stack ?? 0)) return doAllIn();
    client.act(a?.bet ? 'bet' : 'raise', v);
  };

  // горячие клавиши (латиница и русская раскладка)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || confirm) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (canEditDirection) {
        if (e.key === 'ArrowUp') (e.preventDefault(), client.forecast({ direction: 'up' }));
        if (e.key === 'ArrowDown') (e.preventDefault(), client.forecast({ direction: 'down' }));
      }
      if (canEditForecast) {
        if (e.key === '+' || e.key === '=') stepPoints(e.shiftKey ? 4 * tick : tick);
        if (e.key === '-' || e.key === '_') stepPoints(e.shiftKey ? -4 * tick : -tick);
      }
      if (!a) return;
      if (k === 'f' || k === 'а') doFold();
      if (k === 'c' || k === 'с') doCheckCall();
      if (k === 'b' || k === 'и' || k === 'r' || k === 'к') doBetRaise();
      if ((k === 'a' || k === 'ф') && a.allIn) doAllIn();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const lockLeft = secondsLeft(game.lockAt, now);
  const pointsRequired = game.stage === 'round3';
  const missingPoints = canEditForecast && me.forecast.points === null;
  const missingDir = canEditForecast && me.forecast.direction === null;
  // фокус внимания: мигающая рамка на том, что нужно выбрать сейчас (пока игрок не выбрал)
  const attentionDir = missingDir && canEditDirection;
  const attentionPoints = missingPoints && round >= 2;

  if (!me.participant) {
    return (
      <section className="panel controls">
        <p className="muted-line">{t('table.controls.spectator')}</p>
      </section>
    );
  }

  return (
    <section className="panel controls" aria-label={t('table.controls.aria')}>
      {/* ── Прогноз ── */}
      <div className="ctl">
        <CtlHead title={t('table.controls.forecast')}>
          {locked ? (
            <span className="state">{t('table.controls.forecastLocked')}</span>
          ) : folded ? (
            <span className="state">{t('table.controls.youFolded')}</span>
          ) : pointsRequired ? (
            <span className="state warn">{t('table.controls.lockIn', { time: mmss(lockLeft) })}</span>
          ) : null}
        </CtlHead>
        <div className={`dir-toggle ${attentionDir ? 'attention' : ''}`} role="radiogroup" aria-label={t('table.controls.direction')}>
          <button
            role="radio"
            aria-checked={me.forecast.direction === 'up'}
            className={`dir up ${me.forecast.direction === 'up' ? 'on' : ''}`}
            disabled={!canEditDirection}
            onClick={() => client.forecast({ direction: 'up' })}
          >
            <span>▲ {t('table.controls.up')}</span>
            <kbd>↑</kbd>
          </button>
          <button
            role="radio"
            aria-checked={me.forecast.direction === 'down'}
            className={`dir down ${me.forecast.direction === 'down' ? 'on' : ''}`}
            disabled={!canEditDirection}
            onClick={() => client.forecast({ direction: 'down' })}
          >
            <span>▼ {t('table.controls.down')}</span>
            <kbd>↓</kbd>
          </button>
        </div>
        <div className={`field-row ${pointsRequired && missingPoints ? 'invalid' : ''}`}>
          <label htmlFor="pts">
            <span>{t('table.controls.points')}</span>
            <small>{pointsRequired ? t('table.controls.pointsRequired') : t('table.controls.pointsOptional')}</small>
          </label>
          <div className={`stepper ${attentionPoints ? 'attention' : ''}`}>
            <button onClick={() => stepPoints(-tick)} disabled={!canEditForecast} aria-label={t('table.controls.pointsLess', { tick: pts(tick) })}>
              −
            </button>
            <input
              id="pts"
              inputMode="decimal"
              className="mono"
              placeholder="—"
              value={pointsDraft}
              disabled={!canEditForecast}
              onChange={(e) => setPointsDraft(e.target.value)}
              onBlur={commitDraft}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            />
            <button onClick={() => stepPoints(tick)} disabled={!canEditForecast} aria-label={t('table.controls.pointsMore', { tick: pts(tick) })}>
              +
            </button>
          </div>
        </div>
        <Notice
          items={[
            missingDir &&
              canEditDirection && {
                tone: 'warn',
                text: open ? t('table.controls.noticeChooseDirectionOpen') : t('table.controls.noticeChooseDirection'),
              },
            missingDir &&
              !canEditDirection && {
                tone: 'danger',
                text: open ? t('table.controls.noticeNoDirectionLate') : t('table.controls.noticeNoDirectionRound3'),
              },
            canEditForecast &&
              !missingDir &&
              open &&
              round >= 2 && {
                tone: 'info',
                text: t('table.controls.noticeFixedOpen'),
              },
            canEditForecast &&
              !missingDir &&
              !open &&
              game.stage === 'round2' && {
                tone: 'info',
                text: t('table.controls.noticeRound2'),
              },
            canEditForecast &&
              !missingDir &&
              !open &&
              game.stage === 'round3' && {
                tone: 'info',
                text: t('table.controls.noticeRound3'),
              },
            pointsRequired &&
              missingPoints && {
                tone: 'danger',
                text: t('table.controls.noticeNoPoints', { s: sec(lockLeft) }),
              },
          ]}
        />
      </div>

      {/* ── Ставка ── */}
      <div className="ctl">
        <CtlHead title={t('table.controls.bet')}>
          <TurnChip game={game} now={now} />
        </CtlHead>
        <TurnLine game={game} now={now} />
        <BetStatus game={game} now={now} folded={folded} allIn={allIn} />
        {a && <ActionBar a={a} game={game} amount={amount} setAmount={setAmount} onFold={doFold} onCheckCall={doCheckCall} onBetRaise={doBetRaise} onAllIn={doAllIn} pending={pending} />}
      </div>

      {/* ── Предварительное действие ── */}
      {!locked && !folded && !allIn && <PreAction game={game} />}

      {confirm === 'fold' && (
        <Dialog
          title={t('table.controls.foldTitle')}
          tone="danger"
          confirmText={t('table.controls.foldConfirm')}
          onClose={() => setConfirm(null)}
          onConfirm={() => {
            client.act('fold');
            setConfirm(null);
          }}
        >
          <p>{t('table.controls.foldBody', { amount: meSeat?.committed ?? 0 })}</p>
          <label className="check">
            <input
              type="checkbox"
              checked={!settings.confirmFold}
              onChange={(e) =>
                updateSettings((s) => ({
                  ...s,
                  confirmFold: !e.target.checked,
                }))
              }
            />{' '}
            {t('table.controls.dontAsk')}
          </label>
        </Dialog>
      )}
      {confirm === 'allin' && (
        <AllInDialog
          game={game}
          points={allinPoints}
          setPoints={setAllinPoints}
          onClose={() => setConfirm(null)}
          onConfirm={(p) => {
            if (p !== me.forecast.points) client.forecast({ points: p });
            client.act(a?.call?.allIn && !a.allIn ? 'call' : 'allin');
            setConfirm(null);
          }}
        />
      )}
    </section>
  );
}

function CtlHead({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="ctl-head">
      <h3>{title}</h3>
      {children}
    </div>
  );
}

function Notice({ items }: { items: (false | null | undefined | { tone: 'warn' | 'danger' | 'info'; text: string })[] }) {
  const list = items.filter(Boolean) as { tone: string; text: string }[];
  if (!list.length) return null;
  return (
    <div className="notices">
      {list.map((n) => (
        <p key={n.text} className={`notice ${n.tone}`}>
          {n.text}
        </p>
      ))}
    </div>
  );
}

/** Метка в шапке раздела «Ставка»: чей ход и сколько осталось. */
function TurnChip({ game, now }: { game: GameView; now: number }) {
  const round = ROUND_OF[game.stage];
  if (!round) return null;
  if (game.bettingClosed) return <span className="state">{t('table.controls.bettingClosed', { n: num(round, 0) })}</span>;
  const left = secondsLeft(game.turnEndsAt, now);
  if (game.me.allowed) return <span className="state accent">{t('table.controls.yourTurn', { s: sec(left) })}</span>;
  const who = game.seats.find((s) => s.playerId === game.toActPlayerId);
  return who ? <span className="state">{t('table.controls.theirTurn', { name: who.name, s: sec(left) })}</span> : null;
}

/** Полоса времени текущего хода под шапкой раздела «Ставка» — та же, что у ходящего в списке участников. */
function TurnLine({ game, now }: { game: GameView; now: number }) {
  const turnMs = useApp((s) => s.info?.turnMs ?? 12_000);
  if (!ROUND_OF[game.stage] || game.bettingClosed || game.turnEndsAt === null || !game.toActPlayerId) return null;
  return <TurnBar endsAt={game.turnEndsAt} turnMs={turnMs} now={now} me={!!game.me.allowed} />;
}

/** Строка состояния: почему сейчас нельзя ставить и когда станет можно. */
function BetStatus({ game, now, folded, allIn }: { game: GameView; now: number; folded: boolean; allIn: boolean }) {
  const round = ROUND_OF[game.stage];
  const stageLeft = mmss(secondsLeft(game.stageEndsAt, now));
  const facts = (
    <dl className="bet-facts">
      <div>
        <dt>{t('table.controls.roundBet')}</dt>
        <dd className="mono">{game.currentBet ? cr(game.currentBet) : '—'}</dd>
      </div>
      <div>
        <dt>{t('table.controls.toCall')}</dt>
        <dd className="mono">{game.me.toCall ? cr(game.me.toCall) : '—'}</dd>
      </div>
      <div>
        <dt>{t('table.controls.raises')}</dt>
        <dd className="mono">{round ? t('table.controls.raisesOf', { n: num(game.raisesLeft, 0), max: num(1, 0) }) : '—'}</dd>
      </div>
    </dl>
  );
  let text: string | null = null;
  if (folded) text = t('table.controls.statusFolded');
  else if (allIn) text = t('table.controls.statusAllIn');
  else if (game.stage === 'prep') text = t('table.controls.statusPrep', { time: stageLeft });
  else if (round && game.bettingClosed)
    text =
      round < 3
        ? game.instrument
          ? t('table.controls.statusNextRoundPlain', { time: stageLeft }) // в «Открытых картах» раскрывать нечего
          : t('table.controls.statusNextRound', { time: stageLeft, reveal: t(NEXT_REVEAL[round]!) })
        : t('table.controls.statusLockIn', { time: stageLeft });
  else if (round && !game.me.allowed) text = t('table.controls.statusWait');
  else if (game.me.allowed && game.raisesLeft === 0 && game.currentBet > 0) text = t('table.controls.statusRaiseLimit');
  return (
    <>
      {facts}
      {text && <p className="muted-line">{text}</p>}
    </>
  );
}

function ActionBar(props: {
  a: AllowedActions;
  game: GameView;
  amount: number;
  setAmount: (v: number) => void;
  onFold: () => void;
  onCheckCall: () => void;
  onBetRaise: () => void;
  onAllIn: () => void;
  pending: boolean;
}) {
  const { a, game, amount, setAmount } = props;
  const range = a.bet ?? a.raise ?? null;
  const presets = useMemo(() => {
    if (!range) return [];
    const clamp = (v: number) => Math.min(range.max, Math.max(range.min, Math.round(v / 100) * 100));
    return [
      { label: t('table.controls.presetMin'), v: range.min },
      { label: t('table.controls.presetHalfPot'), v: clamp(game.currentBet + game.pot / 2) },
      { label: t('table.controls.presetPot'), v: clamp(game.currentBet + game.pot) },
      { label: t('table.controls.presetMax'), v: range.max },
    ];
  }, [range?.min, range?.max, game.pot, game.currentBet]);
  const noDirection = !a.call && !a.bet && !a.raise && !a.allIn;

  return (
    <div className="actions">
      {range && (
        <div className="amount">
          <div className="field-row">
            <label htmlFor="amt">
              <span>{a.bet ? t('table.controls.betAmount') : t('table.controls.raiseTo')}</span>
              <small>{t('table.controls.range', { min: range.min, max: range.max })}</small>
            </label>
            <div className="input-suffix">
              <input id="amt" className="mono" type="number" min={range.min / 100} max={range.max / 100} step={1} value={amount / 100} onChange={(e) => setAmount(toMinor(Number(e.target.value)))} />
              <span>{t('common.creditsUnit')}</span>
            </div>
          </div>
          <input type="range" min={range.min} max={range.max} step={100} value={amount} onChange={(e) => setAmount(Number(e.target.value))} aria-label={t('table.controls.betAmount')} />
          <div className="segmented four">
            {presets.map((p) => (
              <button key={p.label} className={amount === p.v ? 'on' : ''} onClick={() => setAmount(p.v)}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="action-grid">
        <button className="btn danger-outline" onClick={props.onFold} disabled={props.pending}>
          <span>{t('table.controls.fold')}</span>
          <kbd>F</kbd>
        </button>
        <button className="btn" onClick={props.onCheckCall} disabled={props.pending || (!a.check && !a.call)}>
          <span>{a.check ? t('table.controls.check') : a.call ? `${t('table.controls.call')} ${cr(a.call.amount)}` : t('table.controls.call')}</span>
          <kbd>C</kbd>
        </button>
        <button className="btn primary" onClick={props.onBetRaise} disabled={props.pending || !range}>
          <span>
            {a.raise || (!a.bet && game.currentBet > 0) ? t('table.controls.raise') : t('table.controls.betAction')}
            {range ? ` ${cr(amount)}` : ''}
          </span>
          <kbd>B</kbd>
        </button>
        <button className="btn accent-outline" onClick={props.onAllIn} disabled={props.pending || !a.allIn}>
          <span>
            {t('table.controls.allIn')}
            {a.allIn ? ` ${cr(a.allIn.amount)}` : ''}
          </span>
          <kbd>A</kbd>
        </button>
      </div>
      {noDirection && <p className="notice warn">{t('table.controls.noDirection')}</p>}
    </div>
  );
}

/** Варианты предварительного действия: подпись кнопки и пояснение (всплывающая подсказка и строка под кнопками). */
const preOptions = (): { mode: AutoMode; label: string; hint: string }[] => [
  { mode: 'none', label: t('table.pre.none'), hint: t('table.pre.noneHint') },
  { mode: 'checkOnly', label: t('table.pre.checkOnly'), hint: t('table.pre.checkOnlyHint') },
  { mode: 'checkFold', label: t('table.pre.checkFold'), hint: t('table.pre.checkFoldHint') },
  { mode: 'callUpTo', label: t('table.pre.callUpTo'), hint: t('table.pre.callUpToHint') },
];

/**
 * Одноразовое действие на ближайший ход (как «чек/пас» в покерных клиентах). Настраивается в любой
 * момент до фиксации; во время собственного хода — на следующий ход.
 */
function PreAction({ game }: { game: GameView }) {
  const auto = game.me.autoAction;
  const [limit, setLimit] = useState<number>(auto.limit ? auto.limit / 100 : game.ante / 100);
  const myTurn = !!game.me.allowed;
  const set = (mode: AutoMode) => client.auto(mode === 'callUpTo' ? { mode, limit: toMinor(limit) } : { mode });
  const options = preOptions();
  const current = options.find((o) => o.mode === auto.mode) ?? options[0]!;
  return (
    <div className="ctl">
      <CtlHead title={t('table.pre.title')}>{auto.mode !== 'none' && <span className="state accent">{t('table.pre.planned')}</span>}</CtlHead>
      <div className="segmented grid2" role="radiogroup" aria-label={t('table.pre.title')}>
        {options.map((o) => (
          <button key={o.mode} role="radio" aria-checked={auto.mode === o.mode} className={auto.mode === o.mode ? 'on' : ''} title={o.hint} onClick={() => set(o.mode)}>
            {o.label}
          </button>
        ))}
      </div>
      {auto.mode === 'callUpTo' && (
        <div className="field-row">
          <label htmlFor="autolim">
            <span>{t('table.pre.limit')}</span>
            <small>{t('table.pre.limitHint')}</small>
          </label>
          <div className="input-suffix">
            <input
              id="autolim"
              className="mono"
              type="number"
              min={1}
              value={limit}
              onChange={(e) => {
                const v = Number(e.target.value);
                setLimit(v);
                if (v > 0) client.auto({ mode: 'callUpTo', limit: toMinor(v) });
              }}
            />
            <span>{t('common.creditsUnit')}</span>
          </div>
        </div>
      )}
      <p className="muted-line">
        {current.hint} {auto.mode !== 'none' && (myTurn ? t('table.pre.onceNext') : t('table.pre.onceNearest'))}
      </p>
    </div>
  );
}

function AllInDialog(props: { game: GameView; points: string; setPoints: (s: string) => void; onClose: () => void; onConfirm: (p: number) => void }) {
  const { game } = props;
  const meSeat = game.seats.find((s) => s.isMe)!;
  const v = Number(props.points.replace(',', '.'));
  const valid = props.points.trim() !== '' && Number.isFinite(v) && v > 0 && Math.abs(v / game.tick - Math.round(v / game.tick)) < 1e-9;
  const dir = game.me.forecast.direction;
  return (
    <Dialog
      title={t('table.controls.allIn')}
      tone="danger"
      confirmText={t('table.allin.confirm', { amount: meSeat.stack })}
      onClose={props.onClose}
      onConfirm={valid && dir ? () => props.onConfirm(v) : undefined}
      confirmDisabled={!valid || !dir}
    >
      <p>
        {t('table.allin.bodyBefore')} <b>{cr(meSeat.stack)}</b> {t('table.allin.bodyAfter')}
      </p>
      <dl className="kv">
        <div>
          <dt>{t('table.controls.direction')}</dt>
          <dd className={dir === 'up' ? 'up' : dir === 'down' ? 'down' : ''}>
            {dir === 'up' ? '▲ ' + t('table.allin.up') : dir === 'down' ? '▼ ' + t('table.allin.down') : t('table.allin.noDirection')}
          </dd>
        </div>
      </dl>
      <label className="field">
        {t('table.allin.points')}
        <input className="mono" inputMode="decimal" value={props.points} onChange={(e) => props.setPoints(e.target.value)} placeholder={t('table.allin.pointsPlaceholder', { tick: pts(game.tick) })} data-autofocus />
      </label>
      {!valid && props.points !== '' && <p className="notice warn">{t('table.allin.pointsInvalid', { tick: pts(game.tick) })}</p>}
    </Dialog>
  );
}
